import { Button as ChakraButton, ButtonProps as ChakraButtonProps } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React from 'react';

import { forwardRef } from '../../system';
import Icon from '../Icons/Icon';
import SiteLogo from '../SiteLogo/SiteLogo';

import ButtonSpinner from './ButtonSpinner';
import { iconSizeMap, iconSpacingMap, iconPaddingMap } from './ButtonTheme';
import DisplayNames from './enums';

export const sizePropTypes = ['xs', 'sm', 'md', 'lg'];
export const variantPropTypes = ['primary', 'secondary', 'default', 'outline', 'ghost'];
export const colorSchemePropTypes = ['primary', 'destructive', 'tertiary'];

const propTypes = {
  children: PropTypes.node.isRequired,
  fillColor: PropTypes.string,
  leftIcon: PropTypes.string,
  rightIcon: PropTypes.string,
  size: PropTypes.oneOf(sizePropTypes),
  variant: PropTypes.oneOf(variantPropTypes),
  iconSize: PropTypes.string,
  colorScheme: PropTypes.oneOf(colorSchemePropTypes),
  leftLogo: PropTypes.string,
  rightLogo: PropTypes.string,
  roundLogo: PropTypes.bool,
  hideLogoBackground: PropTypes.bool
};

export const defaultProps = {
  leftIcon: '',
  rightIcon: '',
  size: 'md',
  variant: 'default',
  iconSize: '',
  loadingText: '',
  leftLogo: '',
  rightLogo: '',
  roundLogo: false,
  hideLogoBackground: false,
  isDisabled: false
};

export type ButtonProps = Omit<ChakraButtonProps, 'leftIcon' | 'rightIcon'> & {
  fillColor?: string;
  leftIcon?: string;
  rightIcon?: string;
  iconSize?: string;
  leftLogo?: string;
  rightLogo?: string;
  roundLogo?: boolean;
  hideLogoBackground?: boolean;
};

const Button = forwardRef<ButtonProps, 'button'>(
  (props: ButtonProps & typeof defaultProps, ref): React.ReactElement => {
    const {
      children,
      variant,
      fill,
      fillColor,
      size,
      leftIcon,
      rightIcon,
      iconSize,
      isLoading,
      loadingText,
      leftLogo,
      rightLogo,
      roundLogo,
      hideLogoBackground,
      ...rest
    } = props;

    const getButtonIcon = (iconType: string, logoType: string, boxSize: string) => {
      let buttonIcon;
      if (iconType) {
        buttonIcon = <Icon boxSize={boxSize} type={iconType} />;
      } else if (logoType) {
        buttonIcon = (
          <SiteLogo boxSize={boxSize} hideBackground={hideLogoBackground} isRound={roundLogo} type={logoType} />
        );
      }

      return buttonIcon;
    };

    const sharedProps = { size, isLoading, loadingText };
    const buttonProps = {
      children: isLoading ? loadingText : children,
      fill: fillColor || fill,
      variant,
      paddingLeft: leftIcon && iconPaddingMap[size],
      paddingRight: rightIcon && iconPaddingMap[size],
      leftIcon: getButtonIcon(leftIcon, leftLogo, iconSize || iconSizeMap[size]),
      rightIcon: getButtonIcon(rightIcon, rightLogo, iconSize || iconSizeMap[size]),
      iconSpacing: iconSpacingMap[props.size],
      spinner: isLoading ? <ButtonSpinner {...sharedProps} /> : undefined,
      ref,
      ...sharedProps,
      ...rest
    };

    return <ChakraButton {...buttonProps} />;
  }
);

Button.displayName = DisplayNames.Button;
Button.propTypes = propTypes;
Button.defaultProps = defaultProps;

export default Button;
